/**
 * District definitions (spec section 6).
 *
 * MVP uses 5 districts. Fields beyond landPrice/name are used from later phases
 * (demand, pricing) but are defined now so the data is stable. Peak hours are
 * [startHour, endHour] pairs in 24h time.
 */
export interface District {
  id: string;
  name: string;
  landPrice: number; // cost to open a lot here (deducted from cash)
  rentMultiplier: number;
  baseDemand: number; // cars/hour at peak, before modifiers (Phase 5)
  trafficLevel: number; // 0..1
  incomeLevel: number; // 0..1 (affects price tolerance)
  walkingSensitivity: number; // 0..1
  peakHours: Array<[number, number]>;
  weekdayMultiplier: number;
  weekendMultiplier: number;
  color: number; // render tint for the city map
}

export const DISTRICTS: District[] = [
  {
    id: 'downtown',
    name: 'Downtown',
    landPrice: 800_000,
    rentMultiplier: 2.0,
    baseDemand: 120,
    trafficLevel: 0.9,
    incomeLevel: 0.8,
    walkingSensitivity: 0.5,
    peakHours: [[8, 19]],
    weekdayMultiplier: 1.0,
    weekendMultiplier: 0.8,
    color: 0x5c6bc0,
  },
  {
    id: 'office',
    name: 'Office',
    landPrice: 500_000,
    rentMultiplier: 1.4,
    baseDemand: 100,
    trafficLevel: 0.7,
    incomeLevel: 0.75,
    walkingSensitivity: 0.5,
    peakHours: [
      [7, 9],
      [16, 19],
    ],
    weekdayMultiplier: 1.0,
    weekendMultiplier: 0.3,
    color: 0x26a69a,
  },
  {
    id: 'shopping',
    name: 'Shopping',
    landPrice: 600_000,
    rentMultiplier: 1.6,
    baseDemand: 90,
    trafficLevel: 0.8,
    incomeLevel: 0.6,
    walkingSensitivity: 0.7,
    peakHours: [[11, 21]],
    weekdayMultiplier: 0.7,
    weekendMultiplier: 1.4,
    color: 0xec407a,
  },
  {
    id: 'residential',
    name: 'Residential',
    landPrice: 300_000,
    rentMultiplier: 0.9,
    baseDemand: 60,
    trafficLevel: 0.5,
    incomeLevel: 0.5,
    walkingSensitivity: 0.6,
    peakHours: [
      [7, 9],
      [18, 22],
    ],
    weekdayMultiplier: 1.0,
    weekendMultiplier: 1.0,
    color: 0x66bb6a,
  },
  {
    id: 'hospital',
    name: 'Hospital',
    landPrice: 450_000,
    rentMultiplier: 1.2,
    baseDemand: 80,
    trafficLevel: 0.6,
    incomeLevel: 0.55,
    walkingSensitivity: 0.4,
    peakHours: [[9, 20]],
    weekdayMultiplier: 1.0,
    weekendMultiplier: 0.9,
    color: 0xef5350,
  },
];

export function getDistrict(id: string): District | undefined {
  return DISTRICTS.find((d) => d.id === id);
}
